import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Renderer2
} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';

@Directive({
  selector: '[appMobileFocusManager]'
})
export class MobileFocusManagerDirective implements AfterViewInit, OnDestroy {
  private readonly mobileBreakpoint = 960;
  private readonly overlayPanelSelector = '.p-multiselect-panel, .p-dropdown-panel, .p-autocomplete-panel';
  @Input('appMobileFocusManagerTopOffset') appMobileFocusManagerTopOffset = 0;

  private activeField: HTMLElement | null = null;
  private alignActiveFieldToTop = false;
  private lastOverlayTrigger: HTMLElement | null = null;
  private lockedOverlayTrigger: HTMLElement | null = null;
  private topRoomHost: HTMLElement | null = null;
  private topRoomSpacer: HTMLElement | null = null;

  private focusInUnlisten?: () => void;
  private focusOutUnlisten?: () => void;
  private resizeUnlisten?: () => void;
  private pointerDownUnlisten?: () => void;
  private clickUnlisten?: () => void;
  private capturePointerDownListener?: (event: Event) => void;
  private captureMouseDownListener?: (event: Event) => void;
  private captureTouchStartListener?: (event: Event) => void;
  private captureScrollListener?: (event: Event) => void;
  private panelConstraintTimerIds: number[] = [];
  private panelConstraintRafId: number | null = null;
  private releaseFocusStateTimerId: number | null = null;
  private deferredOverlayOpenTimerId: number | null = null;
  private deferredOpenTrigger: HTMLElement | null = null;
  private deferredOpenLockUntil = 0;
  private visualViewportResizeHandler?: () => void;
  private visualViewportScrollHandler?: () => void;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly host: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.focusInUnlisten = this.renderer.listen(this.document, 'focusin', (event: FocusEvent) => {
        this.handleFocusIn(event);
      });
      this.focusOutUnlisten = this.renderer.listen(this.document, 'focusout', () => {
        this.handleFocusOut();
      });
      this.pointerDownUnlisten = this.renderer.listen(this.document, 'pointerdown', (event: PointerEvent) => {
        this.captureOverlayTrigger(event);
      });
      this.clickUnlisten = this.renderer.listen(this.document, 'click', (event: MouseEvent) => {
        this.interceptDeferredOverlayClick(event);
      });
      this.capturePointerDownListener = (event: Event) => this.captureDeferredOverlayOpen(event);
      this.captureMouseDownListener = (event: Event) => this.captureDeferredOverlayOpen(event);
      this.captureTouchStartListener = (event: Event) => this.captureDeferredOverlayOpen(event);
      this.captureScrollListener = (event: Event) => this.handleViewportOrContainerScroll(event);
      this.document.addEventListener('pointerdown', this.capturePointerDownListener, {capture: true, passive: false});
      this.document.addEventListener('mousedown', this.captureMouseDownListener, {capture: true, passive: false});
      this.document.addEventListener('touchstart', this.captureTouchStartListener, {capture: true, passive: false});
      this.document.addEventListener('scroll', this.captureScrollListener, {capture: true, passive: true});
      this.resizeUnlisten = this.renderer.listen('window', 'resize', () => {
        this.scheduleEnsureActiveFieldVisible(80);
        this.scheduleConstrainOpenPanels(80);
      });

      if (window.visualViewport) {
        this.visualViewportResizeHandler = () => {
          this.scheduleEnsureActiveFieldVisible(80);
          this.scheduleConstrainOpenPanels(80);
        };
        this.visualViewportScrollHandler = () => {
          this.scheduleConstrainOpenPanelsOnNextFrame();
        };
        window.visualViewport.addEventListener('resize', this.visualViewportResizeHandler, {passive: true});
        window.visualViewport.addEventListener('scroll', this.visualViewportScrollHandler, {passive: true});
      }
    });
  }

  ngOnDestroy(): void {
    this.focusInUnlisten?.();
    this.focusOutUnlisten?.();
    this.pointerDownUnlisten?.();
    this.clickUnlisten?.();
    this.resizeUnlisten?.();

    if (this.capturePointerDownListener) {
      this.document.removeEventListener('pointerdown', this.capturePointerDownListener, {capture: true} as EventListenerOptions);
      this.capturePointerDownListener = undefined;
    }
    if (this.captureMouseDownListener) {
      this.document.removeEventListener('mousedown', this.captureMouseDownListener, {capture: true} as EventListenerOptions);
      this.captureMouseDownListener = undefined;
    }
    if (this.captureTouchStartListener) {
      this.document.removeEventListener('touchstart', this.captureTouchStartListener, {capture: true} as EventListenerOptions);
      this.captureTouchStartListener = undefined;
    }
    if (this.captureScrollListener) {
      this.document.removeEventListener('scroll', this.captureScrollListener, {capture: true} as EventListenerOptions);
      this.captureScrollListener = undefined;
    }

    if (this.visualViewportResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.visualViewportResizeHandler);
    }
    if (this.visualViewportScrollHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('scroll', this.visualViewportScrollHandler);
    }

    if (this.releaseFocusStateTimerId !== null) {
      window.clearTimeout(this.releaseFocusStateTimerId);
    }
    if (this.panelConstraintRafId !== null) {
      window.cancelAnimationFrame(this.panelConstraintRafId);
      this.panelConstraintRafId = null;
    }
    this.clearPanelConstraintTimers();
    this.clearDeferredOverlayOpenTimer();
    this.clearDeferredOpenLock();

    this.resetAllPanelConstraints();
    this.resetTopRoom();
    this.lastOverlayTrigger = null;
    this.lockedOverlayTrigger = null;
  }

  private handleFocusIn(event: FocusEvent): void {
    if (!this.isMobileViewport()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const container = this.getActiveContainer();
    if (!container) {
      return;
    }

    this.scheduleConstrainOpenPanels(0);
    this.scheduleConstrainOpenPanels(80);
    this.scheduleConstrainOpenPanels(220);

    if (this.isOverlaySearchInput(target)) {
      const anchor = this.resolveOverlayAnchor(target, container)
        ?? this.normalizeTriggerCandidate(this.lockedOverlayTrigger)
        ?? this.normalizeTriggerCandidate(this.lastOverlayTrigger);
      if (!anchor || !container.contains(anchor)) {
        return;
      }

      const shouldRealign = this.activeField !== anchor;
      this.activeField = anchor;
      this.alignActiveFieldToTop = true;
      this.lockedOverlayTrigger = anchor;
      container.classList.add('mobile-focus-active');

      if (shouldRealign) {
        this.scheduleEnsureActiveFieldVisible(0);
        this.scheduleEnsureActiveFieldVisible(100);
        this.scheduleEnsureActiveFieldVisible(240);
      }
      return;
    }

    if (!container.contains(target)) {
      return;
    }

    if (this.isOverlayTrigger(target)) {
      const autocompleteAnchor = target.closest('.p-autocomplete-dropdown')
        ? (target.closest('.p-autocomplete') as HTMLElement | null)
        : null;
      const anchor = (target.closest('.p-dropdown, .p-multiselect') as HTMLElement | null)
        ?? autocompleteAnchor
        ?? this.resolveOverlayAnchor(target, container);
      if (!anchor) {
        return;
      }

      this.activeField = anchor;
      this.alignActiveFieldToTop = true;
      this.lastOverlayTrigger = anchor;
      this.lockedOverlayTrigger = anchor;
      container.classList.add('mobile-focus-active');
      this.scheduleEnsureActiveFieldVisible(0);
      this.scheduleEnsureActiveFieldVisible(100);
      this.scheduleEnsureActiveFieldVisible(240);
      return;
    }

    const focusTarget = this.resolveFocusTarget(target);
    if (!focusTarget) {
      return;
    }

    this.activeField = focusTarget;
    this.alignActiveFieldToTop = false;
    this.lockedOverlayTrigger = null;
    container.classList.add('mobile-focus-active');
    this.scheduleEnsureActiveFieldVisible(0);
    this.scheduleEnsureActiveFieldVisible(100);
    this.scheduleEnsureActiveFieldVisible(240);
  }

  private handleFocusOut(): void {
    if (this.releaseFocusStateTimerId !== null) {
      window.clearTimeout(this.releaseFocusStateTimerId);
    }

    this.releaseFocusStateTimerId = window.setTimeout(() => {
      const container = this.getActiveContainer();
      if (!container) {
        this.activeField = null;
        return;
      }

      const currentActiveElement = this.document.activeElement as HTMLElement | null;
      if (currentActiveElement && currentActiveElement.closest(this.overlayPanelSelector)) {
        this.constrainOpenPanels(container);
        return;
      }

      if (this.hasVisibleOverlayPanel(container)) {
        const anchor = this.normalizeTriggerCandidate(this.lockedOverlayTrigger)
          ?? this.normalizeTriggerCandidate(this.lastOverlayTrigger);
        if (anchor && container.contains(anchor)) {
          this.activeField = anchor;
          this.alignActiveFieldToTop = true;
          container.classList.add('mobile-focus-active');
        }
        this.constrainOpenPanels(container);
        return;
      }

      if (currentActiveElement && container.contains(currentActiveElement) && this.resolveFocusTarget(currentActiveElement)) {
        this.activeField = this.resolveFocusTarget(currentActiveElement);
        return;
      }

      this.activeField = null;
      this.alignActiveFieldToTop = false;
      this.lockedOverlayTrigger = null;
      container.classList.remove('mobile-focus-active');
      this.resetAllPanelConstraints();
      this.clearDeferredOverlayOpenTimer();
      this.clearDeferredOpenLock();
      this.resetTopRoom();
    }, 80);
  }

  private scheduleEnsureActiveFieldVisible(delayMs: number): void {
    window.setTimeout(() => this.ensureActiveFieldVisible(), delayMs);
  }

  private ensureActiveFieldVisible(): void {
    if (!this.activeField || !this.isMobileViewport()) {
      return;
    }

    const container = this.getActiveContainer();
    if (!container || !container.contains(this.activeField)) {
      return;
    }

    const currentActiveElement = this.document.activeElement as HTMLElement | null;
    if (currentActiveElement && currentActiveElement.closest(this.overlayPanelSelector)) {
      this.constrainOpenPanels(container);
      return;
    }

    this.ensureFieldVisible(container, this.activeField, this.alignActiveFieldToTop);
    this.constrainOpenPanels(container);
  }

  private ensureFieldVisible(container: HTMLElement, field: HTMLElement, preferTop: boolean): void {
    const scrollContainer = this.resolveScrollContainer(container, field);
    if (!scrollContainer || !field.isConnected || field.offsetParent === null) {
      return;
    }

    const isPageScroll = scrollContainer === this.document.scrollingElement;
    const scrollRect = !isPageScroll ? scrollContainer.getBoundingClientRect() : null;
    const containerTop = scrollRect?.top ?? 0;
    const containerBottom = scrollRect?.bottom ?? (window.visualViewport?.height || window.innerHeight);
    const fieldRect = field.getBoundingClientRect();

    const keyboardHeight = this.readCssPx('--keyboard-height');
    const safeAreaBottom = this.readCssPx('--safe-area-inset-bottom');
    const topOffset = this.resolveTopOffsetForElement(field, container);
    const topGuard = this.getTopGuardBottom(container) + topOffset;
    const visibleTop = Math.max(containerTop + 8, topGuard + 10);
    const visibleBottom = containerBottom - Math.max(16, keyboardHeight + safeAreaBottom + 8);

    const currentScrollTop = scrollContainer.scrollTop;
    let nextScrollTop = currentScrollTop;

    if (preferTop) {
      this.resetTopRoom();
      const desiredTop = visibleTop + 14;
      const deltaToTop = fieldRect.top - desiredTop;
      nextScrollTop = currentScrollTop + deltaToTop;

      const nextBottom = fieldRect.bottom - deltaToTop;
      if (nextBottom > visibleBottom) {
        nextScrollTop += nextBottom - visibleBottom;
      }
    } else {
      if (topGuard > containerTop + 1) {
        const requiredTopRoom = Math.max(12, Math.round(visibleTop - containerTop + 6));
        this.ensureTopRoom(scrollContainer, requiredTopRoom);
      }

      const refreshedFieldRect = field.getBoundingClientRect();
      const topPadding = Math.max(14, Math.min(32, refreshedFieldRect.height * 0.5));
      if (refreshedFieldRect.bottom > visibleBottom) {
        nextScrollTop += refreshedFieldRect.bottom - visibleBottom;
      }

      const appliedDelta = nextScrollTop - currentScrollTop;
      const predictedTop = refreshedFieldRect.top - appliedDelta;
      if (predictedTop < visibleTop + topPadding) {
        nextScrollTop -= (visibleTop + topPadding) - predictedTop;
      }
    }

    const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    const clamped = Math.max(0, Math.min(nextScrollTop, maxScrollTop));

    if (Math.abs(clamped - currentScrollTop) > 1) {
      scrollContainer.scrollTo({top: clamped, behavior: 'auto'});
    }
  }

  private captureOverlayTrigger(event: Event): void {
    if (!this.isMobileViewport()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (target.closest('.p-dropdown-clear-icon, .p-multiselect-clear-icon, .p-dropdown-filter-clear-icon')) {
      return;
    }

    const container = this.getActiveContainer();
    if (!container || !container.contains(target)) {
      return;
    }

    const explicitAutoTrigger = target.closest('.p-autocomplete-dropdown') as HTMLElement | null;
    const trigger = (target.closest('.p-multiselect, .p-dropdown') as HTMLElement | null)
      ?? (explicitAutoTrigger ? (explicitAutoTrigger.closest('.p-autocomplete') as HTMLElement | null) : null);

    if (!trigger) {
      return;
    }

    this.lastOverlayTrigger = trigger;
    this.lockedOverlayTrigger = trigger;
    this.activeField = trigger;
    this.alignActiveFieldToTop = true;
    container.classList.add('mobile-focus-active');

    this.scheduleEnsureActiveFieldVisible(0);
    this.scheduleEnsureActiveFieldVisible(80);
    this.scheduleEnsureActiveFieldVisible(220);
    this.scheduleConstrainOpenPanels(40);
    this.scheduleConstrainOpenPanels(140);

    if (this.resolveDeferOpenEnabled(trigger, container) && !this.isPanelOpenForTrigger(trigger)) {
      this.preventOverlayOpenEvent(event);
      this.lockDeferredOpen(trigger);
      this.scheduleDeferredOverlayOpen(trigger, container, this.resolveTriggerScrollDelay(trigger, container));
    }
  }

  private captureDeferredOverlayOpen(event: Event): void {
    if (!this.isMobileViewport()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (target.closest('.p-dropdown-clear-icon, .p-multiselect-clear-icon, .p-dropdown-filter-clear-icon')) {
      return;
    }

    const container = this.getActiveContainer();
    if (!container || !container.contains(target)) {
      return;
    }

    const explicitAutoTrigger = target.closest('.p-autocomplete-dropdown') as HTMLElement | null;
    const trigger = (target.closest('.p-multiselect, .p-dropdown') as HTMLElement | null)
      ?? (explicitAutoTrigger ? (explicitAutoTrigger.closest('.p-autocomplete') as HTMLElement | null) : null);
    if (!trigger) {
      return;
    }

    if (!this.resolveDeferOpenEnabled(trigger, container) || this.isPanelOpenForTrigger(trigger)) {
      return;
    }

    this.preventOverlayOpenEvent(event);
    this.lastOverlayTrigger = trigger;
    this.lockedOverlayTrigger = trigger;
    this.activeField = trigger;
    this.alignActiveFieldToTop = true;
    container.classList.add('mobile-focus-active');

    const scrollDelay = this.resolveTriggerScrollDelay(trigger, container);
    this.scheduleEnsureActiveFieldVisible(0);
    this.scheduleEnsureActiveFieldVisible(scrollDelay);
    this.scheduleEnsureActiveFieldVisible(scrollDelay + 120);
    this.scheduleConstrainOpenPanels(40);
    this.scheduleConstrainOpenPanels(scrollDelay + 140);
    this.lockDeferredOpen(trigger);
    this.scheduleDeferredOverlayOpen(trigger, container, scrollDelay);
  }

  private scheduleDeferredOverlayOpen(trigger: HTMLElement, container: HTMLElement, firstScrollDelay: number): void {
    this.clearDeferredOverlayOpenTimer();
    const openDelay = Math.max(160, firstScrollDelay + 120);

    this.deferredOverlayOpenTimerId = window.setTimeout(() => {
      this.deferredOverlayOpenTimerId = null;
      if (!this.isMobileViewport()) {
        return;
      }

      const normalizedTrigger = this.normalizeTriggerCandidate(trigger);
      if (!normalizedTrigger || !normalizedTrigger.isConnected) {
        this.clearDeferredOpenLock();
        return;
      }

      const activeContainer = this.getActiveContainer();
      if (!activeContainer || (!activeContainer.contains(normalizedTrigger) && activeContainer !== container)) {
        this.clearDeferredOpenLock();
        return;
      }

      this.clearDeferredOpenLock();
      if (!this.isPanelOpenForTrigger(normalizedTrigger)) {
        normalizedTrigger.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true, view: window}));
      }
      this.scheduleEnsureActiveFieldVisible(40);
      this.scheduleEnsureActiveFieldVisible(140);
      this.scheduleConstrainOpenPanels(60);
      this.scheduleConstrainOpenPanels(180);
    }, openDelay);
  }

  private interceptDeferredOverlayClick(event: MouseEvent): void {
    if (!this.isMobileViewport()) {
      return;
    }
    if (!this.deferredOpenTrigger || Date.now() > this.deferredOpenLockUntil) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const trigger = this.normalizeTriggerCandidate(
      target.closest('.p-dropdown, .p-multiselect, .p-autocomplete') as HTMLElement | null
    );
    if (!trigger || trigger !== this.deferredOpenTrigger) {
      return;
    }
    if (this.isPanelOpenForTrigger(trigger)) {
      this.clearDeferredOpenLock();
      return;
    }

    this.preventOverlayOpenEvent(event);
  }

  private handleViewportOrContainerScroll(event: Event): void {
    if (!this.isMobileViewport()) {
      return;
    }

    const target = event.target;
    if (target instanceof Element && this.isOverlayListWrapper(target)) {
      return;
    }

    this.scheduleConstrainOpenPanelsOnNextFrame();
  }

  private scheduleConstrainOpenPanelsOnNextFrame(): void {
    if (this.panelConstraintRafId !== null) {
      return;
    }

    this.panelConstraintRafId = window.requestAnimationFrame(() => {
      this.panelConstraintRafId = null;
      const container = this.getActiveContainer();
      if (!container) {
        return;
      }

      this.constrainOpenPanels(container);
    });
  }

  private getActiveContainer(): HTMLElement | null {
    const hostElement = this.host.nativeElement;
    const hostDialog = hostElement.closest('.p-dialog') as HTMLElement | null;
    if (hostDialog) {
      return hostDialog;
    }

    const dialogs = Array.from(this.document.querySelectorAll<HTMLElement>('.p-dialog.app-modal'));
    const activeDialog = dialogs.find((dialog: HTMLElement) => {
      const style = window.getComputedStyle(dialog);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    if (activeDialog) {
      return activeDialog;
    }

    return hostElement;
  }

  private resolveScrollContainer(container: HTMLElement, field: HTMLElement): HTMLElement | null {
    const dialogContent = container.querySelector('.p-dialog-content') as HTMLElement | null;
    if (dialogContent) {
      return dialogContent;
    }

    let parent: HTMLElement | null = field.parentElement;
    while (parent && parent !== container) {
      const style = window.getComputedStyle(parent);
      const isScrollable = /(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight;
      if (isScrollable) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return this.document.scrollingElement as HTMLElement | null;
  }

  private resolveFocusTarget(target: HTMLElement): HTMLElement | null {
    if (target.closest('.ql-clipboard')) {
      const editorFromClipboard = target.closest('.p-editor, .p-editor-container') as HTMLElement | null;
      if (editorFromClipboard) {
        return editorFromClipboard;
      }
    }

    const editorContainer = target.closest('.p-editor-container, .p-editor-content') as HTMLElement | null;
    if (editorContainer) {
      const editorBlock = editorContainer.closest('.p-editor-container, .p-editor') as HTMLElement | null;
      return editorBlock ?? editorContainer;
    }

    const focusSelector = [
      'input:not([type="hidden"])',
      'textarea',
      'select',
      '[contenteditable="true"]',
      '.p-inputtext'
    ].join(',');

    const directControl = target.closest(focusSelector) as HTMLElement | null;
    if (directControl && this.isVisibleFocusableControl(directControl)) {
      return directControl;
    }

    const wrapper = target.closest('.p-float-label, .p-field') as HTMLElement | null;
    if (wrapper) {
      const visibleControl = wrapper.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, select, [contenteditable="true"], .p-inputtext, .p-editor, .p-editor-container'
      );
      if (visibleControl && this.isVisibleFocusableControl(visibleControl)) {
        return visibleControl;
      }
      return wrapper;
    }

    return null;
  }

  private resolveOverlayAnchor(target: HTMLElement, container: HTMLElement): HTMLElement | null {
    const panel = target.closest(this.overlayPanelSelector) as HTMLElement | null;
    return panel ? this.findBestTriggerForPanel(panel, container) : null;
  }

  private findBestTriggerForPanel(panel: HTMLElement, container: HTMLElement): HTMLElement | null {
    const openTrigger = this.findOpenTriggerForPanel(panel, container);
    if (openTrigger) {
      return openTrigger;
    }

    const lockedTrigger = this.normalizeTriggerCandidate(this.lockedOverlayTrigger);
    if (lockedTrigger && container.contains(lockedTrigger) && this.isMatchingTriggerForPanel(lockedTrigger, panel)) {
      return lockedTrigger;
    }

    const lastTrigger = this.normalizeTriggerCandidate(this.lastOverlayTrigger);
    if (lastTrigger && container.contains(lastTrigger) && this.isMatchingTriggerForPanel(lastTrigger, panel)) {
      return lastTrigger;
    }

    if (panel.id) {
      const controlByAria = this.document.querySelector<HTMLElement>(`[aria-controls="${panel.id}"]`);
      const normalizedAriaTrigger = this.normalizeTriggerCandidate(controlByAria);
      if (normalizedAriaTrigger && container.contains(normalizedAriaTrigger)) {
        return normalizedAriaTrigger;
      }
    }

    const candidates = Array.from(container.querySelectorAll<HTMLElement>('.p-multiselect, .p-dropdown, .p-autocomplete'));
    if (!candidates.length) {
      return null;
    }

    const panelRect = panel.getBoundingClientRect();
    let best: HTMLElement | null = null;
    let bestScore = Number.POSITIVE_INFINITY;

    candidates.forEach((candidate: HTMLElement) => {
      const rect = candidate.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      const verticalDistance = Math.abs(rect.bottom - panelRect.top);
      const horizontalDistance = Math.abs((rect.left + rect.width / 2) - (panelRect.left + panelRect.width / 2));
      const score = verticalDistance + (horizontalDistance * 0.2);

      if (score < bestScore) {
        best = candidate;
        bestScore = score;
      }
    });

    return best;
  }

  private findOpenTriggerForPanel(panel: HTMLElement, container: HTMLElement): HTMLElement | null {
    const ariaTrigger = this.resolveAriaTriggerForPanel(panel, container);
    if (ariaTrigger) {
      return ariaTrigger;
    }

    let selector = '';
    if (panel.classList.contains('p-dropdown-panel')) {
      selector = '.p-dropdown.p-dropdown-open';
    } else if (panel.classList.contains('p-multiselect-panel')) {
      selector = '.p-multiselect.p-multiselect-open';
    } else if (panel.classList.contains('p-autocomplete-panel')) {
      selector = '.p-autocomplete.p-inputwrapper-focus';
    }

    if (!selector) {
      return null;
    }

    const openCandidate = container.querySelector<HTMLElement>(selector);
    return this.normalizeTriggerCandidate(openCandidate);
  }

  private normalizeTriggerCandidate(candidate: HTMLElement | null): HTMLElement | null {
    if (!candidate || !candidate.isConnected) {
      return null;
    }

    const normalized = candidate.closest('.p-dropdown, .p-multiselect, .p-autocomplete') as HTMLElement | null;
    return normalized ?? candidate;
  }

  private isMatchingTriggerForPanel(trigger: HTMLElement, panel: HTMLElement): boolean {
    const ariaTrigger = this.resolveAriaTriggerForPanel(panel);
    if (ariaTrigger) {
      return trigger === ariaTrigger;
    }

    if (panel.classList.contains('p-multiselect-panel')) {
      return trigger.classList.contains('p-multiselect');
    }
    if (panel.classList.contains('p-dropdown-panel')) {
      return trigger.classList.contains('p-dropdown');
    }
    if (panel.classList.contains('p-autocomplete-panel')) {
      return trigger.classList.contains('p-autocomplete');
    }
    return false;
  }

  private resolveAriaTriggerForPanel(panel: HTMLElement, scope?: ParentNode): HTMLElement | null {
    if (!panel.id) {
      return null;
    }

    const root = scope ?? this.document;
    const controller = root.querySelector<HTMLElement>(`[aria-controls="${panel.id}"]`)
      ?? this.document.querySelector<HTMLElement>(`[aria-controls="${panel.id}"]`);

    return this.normalizeTriggerCandidate(controller);
  }

  private getTopGuardBottom(container: HTMLElement): number {
    const modalHeader = container.querySelector('.p-dialog-header') as HTMLElement | null;
    if (modalHeader) {
      return modalHeader.getBoundingClientRect().bottom;
    }

    const selectors = [
      'header',
      '.layout-topbar',
      '.layout-topbar-wrapper',
      '.topbar',
      '.site-topbar',
      '.site-header',
      '.app-header',
      '.sticky-top'
    ];

    let obstructionBottom = 0;
    selectors.forEach((selector: string) => {
      const elements = Array.from(this.document.querySelectorAll<HTMLElement>(selector));
      elements.forEach((element: HTMLElement) => {
        const style = window.getComputedStyle(element);
        if (!['fixed', 'sticky'].includes(style.position)) {
          return;
        }
        const rect = element.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top <= 1) {
          obstructionBottom = Math.max(obstructionBottom, rect.bottom);
        }
      });
    });

    return obstructionBottom;
  }

  private ensureTopRoom(scrollContainer: HTMLElement, minPaddingTopPx: number): void {
    if (this.topRoomHost && this.topRoomHost !== scrollContainer) {
      this.resetTopRoom();
    }
    this.topRoomHost = scrollContainer;

    if (!this.topRoomSpacer || this.topRoomSpacer.parentElement !== scrollContainer) {
      this.topRoomSpacer = this.document.createElement('div');
      this.topRoomSpacer.className = 'mobile-focus-top-room';
      this.topRoomSpacer.setAttribute('aria-hidden', 'true');
      this.topRoomSpacer.style.width = '100%';
      this.topRoomSpacer.style.pointerEvents = 'none';
      this.topRoomSpacer.style.flex = '0 0 auto';
      this.topRoomSpacer.style.height = '0px';
      scrollContainer.insertBefore(this.topRoomSpacer, scrollContainer.firstChild);
    }

    this.topRoomSpacer.style.height = `${Math.max(0, Math.round(minPaddingTopPx))}px`;
  }

  private resetTopRoom(): void {
    if (!this.topRoomHost) {
      return;
    }

    if (this.topRoomSpacer && this.topRoomSpacer.parentElement === this.topRoomHost) {
      this.topRoomHost.removeChild(this.topRoomSpacer);
    }
    this.topRoomHost = null;
    this.topRoomSpacer = null;
  }

  private resolveTopOffsetForElement(element: HTMLElement | null, container: HTMLElement): number {
    const hostOffsetParsed = Number.parseFloat(String(this.appMobileFocusManagerTopOffset ?? 0));
    const hostOffset = Number.isFinite(hostOffsetParsed) ? hostOffsetParsed : 0;
    if (!element) {
      return hostOffset;
    }

    const scoped = element.closest('[data-mobile-focus-top-offset]') as HTMLElement | null;
    if (!scoped || !container.contains(scoped)) {
      return hostOffset;
    }

    return hostOffset + this.readDataNumber(scoped, 'mobileFocusTopOffset', 0);
  }

  private scheduleConstrainOpenPanels(delayMs: number): void {
    const timerId = window.setTimeout(() => {
      this.panelConstraintTimerIds = this.panelConstraintTimerIds.filter((id: number) => id !== timerId);
      const container = this.getActiveContainer();
      if (!container) {
        this.resetAllPanelConstraints();
        return;
      }
      this.constrainOpenPanels(container);
    }, delayMs);

    this.panelConstraintTimerIds.push(timerId);
  }

  private clearPanelConstraintTimers(): void {
    this.panelConstraintTimerIds.forEach((timerId: number) => window.clearTimeout(timerId));
    this.panelConstraintTimerIds = [];
  }

  private constrainOpenPanels(container: HTMLElement): void {
    if (!this.isMobileViewport()) {
      this.resetAllPanelConstraints();
      return;
    }

    const allPanels = Array.from(this.document.querySelectorAll<HTMLElement>(this.overlayPanelSelector));
    if (!allPanels.length) {
      return;
    }

    const visiblePanels = allPanels.filter((panel: HTMLElement) => {
      const style = window.getComputedStyle(panel);
      return style.display !== 'none' && style.visibility !== 'hidden' && panel.getBoundingClientRect().height > 0;
    });

    if (!visiblePanels.length) {
      allPanels.forEach((panel: HTMLElement) => this.resetPanelConstraints(panel));
      return;
    }

    visiblePanels.forEach((panel: HTMLElement) => {
      const trigger = this.getTriggerForPanel(panel, container);
      if (trigger && !container.contains(trigger)) {
        this.resetPanelConstraints(panel);
        return;
      }
      this.constrainPanelHeight(panel);
    });
  }

  private constrainPanelHeight(panel: HTMLElement): void {
    this.saveOriginalStyle(panel, 'data-mobile-focus-original-panel-max-height', 'maxHeight');
    this.saveOriginalStyle(panel, 'data-mobile-focus-original-panel-overflow-y', 'overflowY');

    const panelRect = panel.getBoundingClientRect();
    if (panelRect.height <= 0) {
      return;
    }

    const safeAreaBottom = this.readCssPx('--safe-area-inset-bottom');
    const viewportBottom = this.getViewportBottom();
    const availableHeight = Math.floor(viewportBottom - panelRect.top - Math.max(8, safeAreaBottom));
    const panelMaxHeight = Math.max(120, availableHeight);

    panel.style.maxHeight = `${panelMaxHeight}px`;
    panel.style.overflowY = 'hidden';
    panel.style.setProperty('-webkit-overflow-scrolling', 'touch');

    const listWrapper = panel.querySelector<HTMLElement>(
      '.p-multiselect-items-wrapper, .p-dropdown-items-wrapper, .p-autocomplete-items-wrapper'
    );

    if (!listWrapper) {
      panel.style.overflowY = 'auto';
      return;
    }

    this.saveOriginalStyle(listWrapper, 'data-mobile-focus-original-wrapper-max-height', 'maxHeight');
    this.saveOriginalStyle(listWrapper, 'data-mobile-focus-original-wrapper-overflow-y', 'overflowY');

    const wrapperRect = listWrapper.getBoundingClientRect();
    const chromeHeight = Math.max(48, panelRect.height - wrapperRect.height);
    const wrapperMaxHeight = Math.max(60, panelMaxHeight - chromeHeight);

    listWrapper.style.maxHeight = `${wrapperMaxHeight}px`;
    listWrapper.style.overflowY = 'auto';
    listWrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
  }

  private getViewportBottom(): number {
    if (window.visualViewport) {
      return window.visualViewport.offsetTop + window.visualViewport.height;
    }

    const keyboardHeight = this.readCssPx('--keyboard-height');
    return keyboardHeight > 0 ? window.innerHeight - keyboardHeight : window.innerHeight;
  }

  private isKeyboardLikelyOpen(): boolean {
    const keyboardHeight = this.readCssPx('--keyboard-height');
    const viewportDelta = window.visualViewport
      ? Math.max(0, window.innerHeight - window.visualViewport.height)
      : 0;

    return keyboardHeight > 0 || viewportDelta > 120;
  }

  private saveOriginalStyle(element: HTMLElement, dataKey: string, styleKey: 'maxHeight' | 'overflowY'): void {
    if (element.getAttribute(dataKey) === null) {
      element.setAttribute(dataKey, element.style[styleKey] || '');
    }
  }

  private resetPanelConstraints(panel: HTMLElement): void {
    const originalPanelMaxHeight = panel.getAttribute('data-mobile-focus-original-panel-max-height');
    if (originalPanelMaxHeight !== null) {
      panel.style.maxHeight = originalPanelMaxHeight;
      panel.removeAttribute('data-mobile-focus-original-panel-max-height');
    }

    const originalPanelOverflowY = panel.getAttribute('data-mobile-focus-original-panel-overflow-y');
    if (originalPanelOverflowY !== null) {
      panel.style.overflowY = originalPanelOverflowY;
      panel.removeAttribute('data-mobile-focus-original-panel-overflow-y');
    }

    const listWrapper = panel.querySelector<HTMLElement>(
      '.p-multiselect-items-wrapper, .p-dropdown-items-wrapper, .p-autocomplete-items-wrapper'
    );
    if (!listWrapper) {
      return;
    }

    const originalWrapperMaxHeight = listWrapper.getAttribute('data-mobile-focus-original-wrapper-max-height');
    if (originalWrapperMaxHeight !== null) {
      listWrapper.style.maxHeight = originalWrapperMaxHeight;
      listWrapper.removeAttribute('data-mobile-focus-original-wrapper-max-height');
    }

    const originalWrapperOverflowY = listWrapper.getAttribute('data-mobile-focus-original-wrapper-overflow-y');
    if (originalWrapperOverflowY !== null) {
      listWrapper.style.overflowY = originalWrapperOverflowY;
      listWrapper.removeAttribute('data-mobile-focus-original-wrapper-overflow-y');
    }
  }

  private resetAllPanelConstraints(): void {
    const allPanels = Array.from(this.document.querySelectorAll<HTMLElement>(this.overlayPanelSelector));
    allPanels.forEach((panel: HTMLElement) => this.resetPanelConstraints(panel));
  }

  private getTriggerForPanel(panel: HTMLElement, container: HTMLElement): HTMLElement | null {
    const ariaTrigger = this.resolveAriaTriggerForPanel(panel, container);
    if (ariaTrigger && container.contains(ariaTrigger)) {
      return ariaTrigger;
    }

    const lockedTrigger = this.normalizeTriggerCandidate(this.lockedOverlayTrigger);
    if (lockedTrigger && container.contains(lockedTrigger) && this.isMatchingTriggerForPanel(lockedTrigger, panel)) {
      return lockedTrigger;
    }

    const trigger = this.findBestTriggerForPanel(panel, container);
    if (trigger) {
      return trigger;
    }

    const activeTrigger = this.normalizeTriggerCandidate(this.activeField);
    if (activeTrigger && container.contains(activeTrigger) && this.isMatchingTriggerForPanel(activeTrigger, panel)) {
      return activeTrigger;
    }

    return null;
  }

  private hasVisibleOverlayPanel(container: HTMLElement): boolean {
    const allPanels = Array.from(this.document.querySelectorAll<HTMLElement>(this.overlayPanelSelector));
    return allPanels.some((panel: HTMLElement) => {
      const style = window.getComputedStyle(panel);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }

      const rect = panel.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return false;
      }

      const trigger = this.getTriggerForPanel(panel, container);
      return !!trigger && container.contains(trigger);
    });
  }

  private resolveTriggerScrollDelay(trigger: HTMLElement, container: HTMLElement): number {
    const scoped = trigger.closest('[data-mobile-focus-trigger-scroll-delay]') as HTMLElement | null;
    if (!scoped || !container.contains(scoped)) {
      return 110;
    }

    return Math.max(0, this.readDataNumber(scoped, 'mobileFocusTriggerScrollDelay', 110));
  }

  private resolveDeferOpenEnabled(trigger: HTMLElement, container: HTMLElement): boolean {
    const scoped = trigger.closest('[data-mobile-focus-defer-open]') as HTMLElement | null;
    if (!scoped || !container.contains(scoped)) {
      return true;
    }

    const raw = (scoped.dataset.mobileFocusDeferOpen || '').trim().toLowerCase();
    if (raw === 'false' || raw === '0' || raw === 'no' || raw === 'off') {
      return false;
    }
    if (raw === 'true' || raw === '1' || raw === 'yes' || raw === 'on') {
      return true;
    }
    return true;
  }

  private isPanelOpenForTrigger(trigger: HTMLElement): boolean {
    const panels = Array.from(this.document.querySelectorAll<HTMLElement>(this.overlayPanelSelector));
    return panels.some((panel: HTMLElement) => {
      const style = window.getComputedStyle(panel);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      const rect = panel.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return false;
      }
      return this.isMatchingTriggerForPanel(trigger, panel);
    });
  }

  private lockDeferredOpen(trigger: HTMLElement): void {
    this.deferredOpenTrigger = trigger;
    this.deferredOpenLockUntil = Date.now() + 500;
  }

  private clearDeferredOpenLock(): void {
    this.deferredOpenTrigger = null;
    this.deferredOpenLockUntil = 0;
  }

  private clearDeferredOverlayOpenTimer(): void {
    if (this.deferredOverlayOpenTimerId !== null) {
      window.clearTimeout(this.deferredOverlayOpenTimerId);
      this.deferredOverlayOpenTimerId = null;
    }
  }

  private preventOverlayOpenEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const immediate = (event as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation;
    immediate?.call(event);
  }

  private readDataNumber(element: HTMLElement, dataKey: string, fallback: number): number {
    const rawValue = element.dataset[dataKey];
    if (rawValue === undefined || rawValue === null || rawValue.trim() === '') {
      return fallback;
    }

    const parsed = Number.parseFloat(rawValue);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  private isOverlayTrigger(target: HTMLElement): boolean {
    if (target.closest('.p-dropdown, .p-multiselect')) {
      return true;
    }

    return !!target.closest('.p-autocomplete-dropdown');
  }

  private isOverlaySearchInput(target: HTMLElement): boolean {
    return !!target.closest('.p-multiselect-filter, .p-dropdown-filter, .p-autocomplete-panel input');
  }

  private isOverlayListWrapper(target: Element): boolean {
    return !!target.closest('.p-multiselect-items-wrapper, .p-dropdown-items-wrapper, .p-autocomplete-items-wrapper');
  }

  private readCssPx(variableName: string): number {
    const value = getComputedStyle(this.document.documentElement).getPropertyValue(variableName).trim();
    const parsed = Number.parseFloat(value.replace('px', ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private isVisibleFocusableControl(element: HTMLElement): boolean {
    if (!element.isConnected) {
      return false;
    }
    if (element.closest('.p-hidden-accessible, .ql-clipboard')) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number.parseFloat(style.opacity || '1') === 0) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width < 12 || rect.height < 12) {
      return false;
    }

    return true;
  }
}
